/**
 * Nuova partita (V17) e modifica dalla pagina della partita (V18) finche e
 * programmata, nello stesso modale.
 *
 * La nuova si apre dalle partite (V16), anche dal giorno scelto nel
 * calendario, che arriva con giorno e ora gia compilati ma modificabili;
 * salvata, porta alla pagina della partita, dove si scelgono i convocati. La
 * modifica si apre dalla pagina della partita e, salvata, la ricarica.
 *
 * Il formato arriva precompilato dalla squadra-stagione ed e una copia: il
 * modale lo dice, perche cambiarlo qui per un torneo a tempi ridotti non deve
 * toccare la squadra. L'avversario si sceglie dall'elenco o si scrive. La
 * competizione la scrive solo chi puo gestirle: un nome che coincide con una
 * esistente, a meno di maiuscole e spazi, la riusa, e se era disattivata la
 * riattiva. Gli altri scelgono fra le attive. Una partita gia in una
 * competizione disattivata la conserva senza riattivarla.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Formato } from '@calcio/shared';
import { nomeConAnno, normalizzaNome, partitaSchema, puo } from '@calcio/shared';
import { InfoIcon, OctagonXIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { CampoData } from '../comuni/CampoData.js';
import { CampoSuggerito } from '../comuni/CampoSuggerito.js';
import type { GiornoProposto } from '../comuni/calendario.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { erroriPerCampo, messaggio, useDati } from '../comuni/dati.js';
import { useContesto } from '../navigazione/contesto.js';
import { AVVISO_ANNO } from '../competizioni/avvisi.js';
import { CampiFormato } from './CampiFormato.js';
import {
  daInputDataOra,
  disattivataConNome,
  perInputDataOra,
  sceltaCompetizione,
} from './partita.js';

interface Opzioni {
  formato: Formato;
  competizioni: { id: string; nome: string }[];
  disattivate: { id: string; nome: string }[];
  avversari: string[];
}

interface DettaglioPartita {
  partita: Formato & {
    id: string;
    stato: string;
    dataOra: string;
    avversario: string;
    inCasa: boolean;
    luogo: string | null;
    note: string | null;
    competizioneId: string;
    competizione: string;
  };
}

export function DialogPartita({
  ss,
  aperto,
  onChiudi,
  partitaId,
  giorno,
  onSalvato,
}: {
  ss: string;
  aperto: boolean;
  onChiudi: () => void;
  /** La partita da modificare; senza, e una nuova. */
  partitaId?: string;
  giorno?: GiornoProposto;
  /** Dopo la modifica; la nuova porta invece alla sua pagina. */
  onSalvato?: () => Promise<void> | void;
}) {
  return (
    <Dialog open={aperto} onOpenChange={(a) => !a && onChiudi()}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        {aperto && (
          <Caricamento
            ss={ss}
            partitaId={partitaId}
            giorno={giorno}
            onChiudi={onChiudi}
            onSalvato={onSalvato}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Caricamento({
  ss,
  partitaId,
  giorno,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  partitaId?: string;
  giorno?: GiornoProposto;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const opzioni = useDati<Opzioni>(`/squadre-stagione/${ss}/partite/opzioni`);
  const esistente = useDati<DettaglioPartita>(partitaId ? `/partite/${partitaId}` : '');
  const modifica = Boolean(partitaId);

  const errore = opzioni.errore ?? (partitaId ? esistente.errore : null);
  if (errore || !opzioni.dati || (partitaId && !esistente.dati)) {
    return (
      <>
        <Intestazione modifica={modifica} />
        <StatoCaricamento errore={errore} />
      </>
    );
  }

  const partita = esistente.dati?.partita;
  if (partita && partita.stato !== 'programmata') {
    return (
      <>
        <Intestazione modifica={modifica} />
        <Alert>
          <InfoIcon />
          <AlertTitle>Partita non modificabile</AlertTitle>
          <AlertDescription>
            La partita si modifica solo finché è programmata. Se la formazione è confermata,
            svuotala dalla pagina della partita.
          </AlertDescription>
        </Alert>
      </>
    );
  }

  return (
    <Form
      ss={ss}
      opzioni={opzioni.dati}
      partita={partita}
      giorno={giorno}
      onChiudi={onChiudi}
      onSalvato={onSalvato}
    />
  );
}

function Intestazione({ modifica }: { modifica: boolean }) {
  return (
    <DialogHeader>
      <DialogTitle>{modifica ? 'Modifica partita' : 'Nuova partita'}</DialogTitle>
      <DialogDescription>
        {modifica
          ? 'Data, avversario, competizione, formato e note della partita.'
          : 'I convocati si scelgono dopo, sulla pagina della partita.'}
      </DialogDescription>
    </DialogHeader>
  );
}

function Form({
  ss,
  opzioni,
  partita,
  giorno,
  onChiudi,
  onSalvato,
}: {
  ss: string;
  opzioni: Opzioni;
  partita?: DettaglioPartita['partita'];
  giorno?: GiornoProposto;
  onChiudi: () => void;
  onSalvato?: () => Promise<void> | void;
}) {
  const navigate = useNavigate();
  const { utente } = useContesto();
  // Le competizioni le gestisce l'Admin Squadra: chi non ha il permesso sceglie
  // fra quelle attive e non ne scrive di nuove.
  const creaCompetizioni = puo(utente.assegnazioni, 'competizione.gestisci', ss);
  const predefinita =
    opzioni.competizioni.find((c) => c.nome === 'Campionato')?.nome ??
    opzioni.competizioni[0]?.nome ??
    '';

  // Giorno e ora in due campi, come "2026-09-23" e "18:00", nell'ora italiana.
  const [data, setData] = useState(() =>
    partita ? perInputDataOra(partita.dataOra).slice(0, 10) : (giorno?.data ?? ''),
  );
  const [ora, setOra] = useState(() =>
    partita ? perInputDataOra(partita.dataOra).slice(11) : (giorno?.ora ?? ''),
  );
  const [avversario, setAvversario] = useState(partita?.avversario ?? '');
  const [inCasa, setInCasa] = useState(partita?.inCasa ?? true);
  const [luogo, setLuogo] = useState(partita?.luogo ?? '');
  const [competizione, setCompetizione] = useState(partita?.competizione ?? predefinita);
  const [note, setNote] = useState(partita?.note ?? '');
  const [formato, setFormato] = useState<Formato>(
    partita
      ? {
          nPeriodi: partita.nPeriodi,
          durataPeriodoMin: partita.durataPeriodoMin,
          giocatoriInCampo: partita.giocatoriInCampo,
          maxSostituzioni: partita.maxSostituzioni,
          rientroConsentito: partita.rientroConsentito,
        }
      : opzioni.formato,
  );
  const [errori, setErrori] = useState<Record<string, string>>({});
  const [erroreServer, setErroreServer] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  // Un errore di salvataggio riguarda i valori inviati: cambiandone uno non
  // descrive piu il form che si ha davanti.
  useEffect(() => setErroreServer(null), [data, ora, avversario, competizione, formato]);

  // La competizione attuale della partita vale anche se nel frattempo e stata
  // disattivata: salvare senza cambiarla non la riattiva.
  const sceglibili =
    partita && !opzioni.competizioni.some((c) => c.id === partita.competizioneId)
      ? [...opzioni.competizioni, { id: partita.competizioneId, nome: partita.competizione }]
      : opzioni.competizioni;
  const scelta = sceltaCompetizione(competizione, sceglibili);
  const disattivata =
    'id' in scelta ? undefined : disattivataConNome(competizione, opzioni.disattivate);

  const invia = async (e: FormEvent) => {
    e.preventDefault();
    const esito = partitaSchema.safeParse({
      competizione: scelta,
      dataOra: daInputDataOra(`${data}T${ora}`),
      avversario,
      inCasa,
      luogo,
      note,
      formato,
    });
    if (!esito.success) return setErrori(erroriPerCampo(esito.error));
    setErrori({});
    setErroreServer(null);
    setInCorso(true);
    try {
      const { id } = await chiama<{ id: string }>(
        partita ? `/partite/${partita.id}` : `/squadre-stagione/${ss}/partite`,
        { metodo: partita ? 'PUT' : 'POST', corpo: esito.data },
      );
      if (partita) {
        toast.success('Partita salvata.');
        await onSalvato?.();
        onChiudi();
      } else {
        toast.success('Partita creata.');
        navigate(`/sq/${ss}/partite/${id}`);
      }
    } catch (errore) {
      setErroreServer(messaggio(errore));
      setInCorso(false);
    }
  };

  return (
    <form onSubmit={(e) => void invia(e)} className="flex flex-col gap-6">
      <Intestazione modifica={Boolean(partita)} />

      <FieldGroup>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={Boolean(errori.dataOra)}>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2 flex flex-col gap-3">
                <FieldLabel htmlFor="partita-data">Giorno</FieldLabel>
                <CampoData
                  id="partita-data"
                  valore={data}
                  invalido={Boolean(errori.dataOra)}
                  onCambia={setData}
                />
              </div>
              <div className="flex flex-col gap-3">
                <FieldLabel htmlFor="partita-ora">Ora</FieldLabel>
                <Input
                  id="partita-ora"
                  type="time"
                  value={ora}
                  aria-invalid={Boolean(errori.dataOra)}
                  onChange={(e) => setOra(e.target.value)}
                />
              </div>
            </div>
            <FieldError>{errori.dataOra}</FieldError>
          </Field>
          <Field>
            <FieldLabel>Campo</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              value={inCasa ? 'casa' : 'trasferta'}
              onValueChange={(v) => v && setInCasa(v === 'casa')}
            >
              <ToggleGroupItem value="casa">Casa</ToggleGroupItem>
              <ToggleGroupItem value="trasferta">Trasferta</ToggleGroupItem>
            </ToggleGroup>
          </Field>
        </div>

        <Field data-invalid={Boolean(errori.avversario)}>
          <FieldLabel htmlFor="partita-avversario">Avversario</FieldLabel>
          <CampoSuggerito
            id="partita-avversario"
            valore={avversario}
            opzioni={opzioni.avversari}
            onCambia={setAvversario}
            segnaposto="Scegli o scrivi l'avversario"
            nuovo={(t) => `Usa «${t}»`}
            invalido={Boolean(errori.avversario)}
          />
          <FieldError>{errori.avversario}</FieldError>
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={Boolean(errori.competizione)}>
            <FieldLabel htmlFor="partita-competizione">Competizione</FieldLabel>
            {creaCompetizioni ? (
              <CampoSuggerito
                id="partita-competizione"
                valore={competizione}
                opzioni={opzioni.competizioni.map((c) => c.nome)}
                onCambia={setCompetizione}
                segnaposto="Scegli o scrivi la competizione"
                nuovo={(t) => {
                  const spenta = disattivataConNome(t, opzioni.disattivate);
                  return spenta
                    ? `Riattiva la competizione «${spenta.nome}»`
                    : `Crea la competizione «${t}»`;
                }}
                invalido={Boolean(errori.competizione)}
              />
            ) : (
              <Select value={competizione} onValueChange={setCompetizione}>
                <SelectTrigger
                  id="partita-competizione"
                  className="w-full"
                  aria-invalid={Boolean(errori.competizione)}
                >
                  <SelectValue placeholder="Scegli la competizione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {sceglibili.map((c) => (
                      <SelectItem key={c.id} value={c.nome}>
                        {c.nome}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            )}
            {errori.competizione ? (
              <FieldError>{errori.competizione}</FieldError>
            ) : disattivata ? (
              <FieldDescription>
                «{disattivata.nome}» è disattivata: verrà riattivata al salvataggio.
              </FieldDescription>
            ) : !('id' in scelta) && normalizzaNome(competizione) ? (
              <FieldDescription>
                Nuova competizione: verrà creata al salvataggio.
                {nomeConAnno(competizione) && ` ${AVVISO_ANNO}`}
              </FieldDescription>
            ) : !creaCompetizioni && sceglibili.length === 0 ? (
              <FieldDescription>
                Nessuna competizione attiva: la aggiunge l'Admin Squadra.
              </FieldDescription>
            ) : null}
          </Field>
          <Field data-invalid={Boolean(errori.luogo)}>
            <FieldLabel htmlFor="partita-luogo">Luogo</FieldLabel>
            <Input
              id="partita-luogo"
              value={luogo}
              aria-invalid={Boolean(errori.luogo)}
              onChange={(e) => setLuogo(e.target.value)}
            />
            <FieldError>{errori.luogo}</FieldError>
          </Field>
        </div>
      </FieldGroup>

      <FieldSet>
        <FieldLegend>Formato di gioco</FieldLegend>
        <FieldDescription>
          Copiato dalla squadra: cambiarlo qui vale solo per questa partita.
        </FieldDescription>
        <CampiFormato formato={formato} onCambia={setFormato} errore={errori.formato} />
      </FieldSet>

      <Field data-invalid={Boolean(errori.note)}>
        <FieldLabel htmlFor="partita-note">Note</FieldLabel>
        <Textarea
          id="partita-note"
          value={note}
          aria-invalid={Boolean(errori.note)}
          onChange={(e) => setNote(e.target.value)}
        />
        <FieldError>{errori.note}</FieldError>
      </Field>

      {erroreServer && (
        <Alert variant="destructive">
          <OctagonXIcon />
          <AlertTitle>Salvataggio non riuscito</AlertTitle>
          <AlertDescription>{erroreServer}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onChiudi}>
          Annulla
        </Button>
        <PulsanteInvio type="submit" inCorso={inCorso}>
          {partita ? 'Salva' : 'Crea partita'}
        </PulsanteInvio>
      </DialogFooter>
    </form>
  );
}
